import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import UserAvatar from "../UserAvatar";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const PUBLISHED_SPOT_STATUSES = new Set(["published", "approved"]);

const ACTIVITY_ICONS = {
    blue: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
        </svg>
    ),
    green: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
    ),
    red: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
    ),
    amber: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
    ),
    violet: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 20h9"/>
            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
        </svg>
    ),
    teal: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            <path d="M8 7h8"/>
            <path d="M8 11h6"/>
        </svg>
    ),
};

const ACTIVITY_STATS = [
    { key: "spotsTotal", label: "Добавлено точек", hint: "Ваши места на карте", tone: "blue", to: "/profile/my-spots" },
    { key: "commentsTotal", label: "Отчётов", hint: "О рыбалке на точках", tone: "green", to: "/profile/comments" },
    { key: "blogCommentsTotal", label: "Комментариев", hint: "К статьям блога", tone: "teal", to: "/profile/blog-comments" },
    { key: "likesTotal", label: "Лайков", hint: "Точки, которые вам понравились", tone: "red", to: "/profile/likes" },
    { key: "favoritesTotal", label: "В избранном", hint: "Сохранённые точки", tone: "amber", to: "/profile/favorites" },
    { key: "suggestionsTotal", label: "Правок", hint: "Предложения изменений", tone: "violet", to: "/profile/suggestions" },
];

const MODERATION_LINKS = [
    { key: "spotModeration", label: "Точки", tab: "my-spots" },
    { key: "commentsModeration", label: "Отчёты", tab: "comments" },
    { key: "blogCommentsModeration", label: "Комментарии", tab: "blog-comments" },
    { key: "suggestionsModeration", label: "Правки точек", tab: "suggestions" },
];

const TIP_OF_DAY = "Добавляйте фото места, улова и эхолота к точкам — так другим рыбакам проще оценить условия и выбрать подход.";

function buildImageUrl(path) {
    if (!path || typeof path !== "string") return "";
    if (path.startsWith("http://") || path.startsWith("https://")) return path;
    return `${API_BASE_URL}${path}`;
}

function getSpotPreviewPhoto(spot) {
    if (!spot) return "";
    return (
        spot.placePhotos?.[0]
        || spot.photos?.[0]
        || spot.mainPhoto
        || spot.photo
        || spot.image
        || spot.place?.image
        || ""
    );
}

function spotName(spot) {
    return spot?.name || spot?.title || spot?.place?.name || `Точка #${spot?.id}`;
}

function normalizeSpotStatus(item) {
    if (item?.status) return String(item.status);
    if (item?.published === true) return "published";
    if (item?.published === false) return "moderation_required";
    return "";
}

function normalizeCommentStatus(item) {
    if (item?.status) return String(item.status);
    if (item?.published === true) return "published";
    if (item?.published === false) return "new";
    return "";
}

function suggestionStatus(item) {
    return item?.moderationStatus || item?.status || "";
}

function parseDate(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
}

function formatRelativeTime(value) {
    const date = parseDate(value);
    if (!date) return "";

    const diffMs = Date.now() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "только что";
    if (diffMin < 60) return `${diffMin} мин. назад`;

    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} ч. назад`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "вчера";
    if (diffDays < 7) return `${diffDays} дн. назад`;

    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
}

function countPublishedSpots(spots) {
    return spots.filter((item) => PUBLISHED_SPOT_STATUSES.has(normalizeSpotStatus(item))).length;
}

function countPublishedComments(comments) {
    return comments.filter((item) => normalizeCommentStatus(item) === "published").length;
}

function countPublishedSuggestions(suggestions) {
    return suggestions.filter((item) => {
        const status = suggestionStatus(item);
        return status === "published" || status === "approved";
    }).length;
}

function buildRecentActivity({ spots, comments, blogComments, suggestions }) {
    const events = [];

    spots.forEach((spot) => {
        events.push({
            id: `spot-${spot.id}`,
            at: spot.createdAt || spot.updatedAt,
            label: `Добавлена точка «${spotName(spot)}»`,
            link: `/spots/${spot.id}`,
            tone: "blue",
        });
    });

    comments.forEach((item) => {
        const text = String(item.comment || item.text || "").trim();
        const preview = text.length > 48 ? `${text.slice(0, 48)}…` : text || "Отчёт";
        events.push({
            id: `comment-${item.id}`,
            at: item.createdAt,
            label: `Отчёт: «${preview}»`,
            link: item.spotId ? `/spots/${item.spotId}` : null,
            tone: "green",
        });
    });

    blogComments.forEach((item) => {
        const text = String(item.comment || item.text || "").trim();
        const preview = text.length > 48 ? `${text.slice(0, 48)}…` : text || "Комментарий";
        const articleTitle = item.blog?.title ? ` «${item.blog.title}»` : "";
        events.push({
            id: `blog-comment-${item.id}`,
            at: item.createdAt,
            label: `Комментарий${articleTitle}: «${preview}»`,
            link: item.blog?.slug ? `/blog/${item.blog.slug}` : null,
            tone: "teal",
        });
    });

    suggestions.forEach((item) => {
        events.push({
            id: `suggestion-${item.id}`,
            at: item.createdAt || item.updatedAt,
            label: `Правка: «${item.title || `Предложение #${item.id}`}»`,
            link: `/profile/suggestions/${item.id}`,
            tone: "violet",
        });
    });

    return events
        .filter((item) => parseDate(item.at))
        .sort((a, b) => parseDate(b.at) - parseDate(a.at))
        .slice(0, 8);
}

function ContributionRow({ title, total, published }) {
    return (
        <div className="profile-contribution__row">
            <strong>{title}</strong>
            <span className="profile-contribution__values">
                <span>{total}</span>
                <span className="profile-contribution__sep">/</span>
                <span>{published}</span>
            </span>
        </div>
    );
}

function sumSpotEngagement(spots) {
    if (!spots.length) {
        return { comments: 0, likes: 0, favorites: 0 };
    }

    return spots.reduce(
        (acc, item) => {
            acc.comments += Number(item.commentsCount ?? 0);
            acc.likes += Number(item.likesCount ?? 0);
            acc.favorites += Number(item.favoritesCount ?? 0);
            return acc;
        },
        { comments: 0, likes: 0, favorites: 0 },
    );
}

export default function ProfileOverview({
    user,
    stats,
    spots,
    comments,
    blogComments,
    suggestions,
    favorites,
}) {
    const displayName = user?.username || user?.name || user?.email?.split("@")[0] || "Пользователь";

    const extendedStats = useMemo(() => ({
        ...stats,
        suggestionsTotal: suggestions.length,
    }), [stats, suggestions.length]);

    const moderationTotal = stats.spotModeration
        + stats.commentsModeration
        + stats.blogCommentsModeration
        + stats.suggestionsModeration;

    const popularityTotals = useMemo(() => sumSpotEngagement(spots), [spots]);

    const popularSpot = useMemo(() => {
        if (!spots.length) return null;
        return spots.reduce((best, item) => {
            const score = Number(item.commentsCount ?? 0)
                + Number(item.likesCount ?? 0)
                + Number(item.favoritesCount ?? 0);
            const bestScore = Number(best?.commentsCount ?? 0)
                + Number(best?.likesCount ?? 0)
                + Number(best?.favoritesCount ?? 0);
            return score > bestScore ? item : best;
        }, spots[0]);
    }, [spots]);

    const recentFavorites = useMemo(() => favorites.slice(0, 3), [favorites]);

    const recentActivity = useMemo(
        () => buildRecentActivity({ spots, comments, blogComments, suggestions }),
        [spots, comments, blogComments, suggestions],
    );

    return (
        <div className="profile-dashboard">
            <header className="profile-dashboard__header">
                <div className="profile-dashboard__identity">
                    <UserAvatar
                        avatarUrl={user?.avatarUrl}
                        name={displayName}
                        className="profile-dashboard__avatar"
                        size={96}
                    />
                    <div className="profile-dashboard__meta">
                        <h1 className="profile-dashboard__title">Мой профиль</h1>
                        <p className="profile-dashboard__name">{displayName}</p>
                        <p className="profile-dashboard__email">{user?.email || "Email не указан"}</p>
                    </div>
                </div>
                <aside className="profile-dashboard__cta" aria-label="Призыв к действию">
                    <div>
                        <strong>Поделитесь опытом</strong>
                        <p>
                            Добавляйте точки, фото и отчёты — так другим рыбакам проще найти
                            хорошее место для следующего выезда.
                        </p>
                        <Link className="toolbar-btn profile-dashboard__cta-btn" to="/spots/new">
                            Добавить точку
                        </Link>
                    </div>
                </aside>
            </header>

            <section className="profile-dashboard__section" aria-labelledby="profile-activity-title">
                <h2 id="profile-activity-title">Моя активность</h2>
                <div className="profile-activity-grid">
                    {ACTIVITY_STATS.map((item) => (
                        <Link
                            to={item.to}
                            className={`profile-activity-card profile-activity-card--${item.tone}`}
                            key={item.key}
                        >
                            <div className="profile-activity-card__top">
                                <span className="profile-activity-card__icon">{ACTIVITY_ICONS[item.tone]}</span>
                                <span className="profile-activity-card__value">{extendedStats[item.key] ?? 0}</span>
                            </div>
                            <span className="profile-activity-card__label">{item.label}</span>
                            <span className="profile-activity-card__hint">{item.hint}</span>
                        </Link>
                    ))}
                </div>
            </section>

            <div className="profile-dashboard__grid">
                <section className="profile-panel" aria-labelledby="profile-moderation-title">
                    <h2 id="profile-moderation-title">На модерации</h2>
                    {moderationTotal === 0 ? (
                        <p className="profile-panel__empty">Всё опубликовано — ожидать проверки нечего.</p>
                    ) : (
                        <div className="profile-moderation-list">
                            {MODERATION_LINKS.map((item) => {
                                const count = stats[item.key] ?? 0;
                                if (!count) return null;
                                return (
                                    <Link key={item.key} to={`/profile/${item.tab}`}>
                                        <span>{item.label}</span>
                                        <span className="profile-moderation-list__count">{count}</span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </section>

                <section className="profile-panel" aria-labelledby="profile-contribution-title">
                    <div className="profile-contribution__header">
                        <h2 id="profile-contribution-title">Мой вклад</h2>
                        <span className="profile-contribution__legend">Всего / Опубликовано</span>
                    </div>
                    <div className="profile-contribution">
                        <ContributionRow title="Точки" total={spots.length} published={countPublishedSpots(spots)} />
                        <ContributionRow title="Отчёты" total={comments.length} published={countPublishedComments(comments)} />
                        <ContributionRow title="Комментарии" total={blogComments.length} published={countPublishedComments(blogComments)} />
                        <ContributionRow title="Правки точек" total={suggestions.length} published={countPublishedSuggestions(suggestions)} />
                    </div>
                </section>

                <section className="profile-panel" aria-labelledby="profile-popularity-title">
                    <h2 id="profile-popularity-title">Популярность моего контента</h2>
                    <div className="profile-popularity-stats">
                        <div>
                            <strong>{popularityTotals.comments}</strong>
                            <span>отчётов</span>
                        </div>
                        <div>
                            <strong>{popularityTotals.likes}</strong>
                            <span>лайков</span>
                        </div>
                        <div>
                            <strong>{popularityTotals.favorites}</strong>
                            <span>добавлений в избранное</span>
                        </div>
                    </div>
                    {popularSpot && (popularityTotals.comments + popularityTotals.likes + popularityTotals.favorites > 0) ? (
                        <article className="profile-popular-spot">
                            <div className="profile-popular-spot__thumb">
                                {getSpotPreviewPhoto(popularSpot) ? (
                                    <img src={buildImageUrl(getSpotPreviewPhoto(popularSpot))} alt="" />
                                ) : (
                                    <span aria-hidden>🗺️</span>
                                )}
                            </div>
                            <div className="profile-popular-spot__right">
                                <p className="profile-popular-spot__label">Самая популярная точка</p>
                                <strong>{spotName(popularSpot)}</strong>
                                <div className="profile-popular-spot__stats">
                                    <span className="profile-popular-spot__stat">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                        {popularSpot.commentsCount ?? 0}
                                    </span>
                                    <span className="profile-popular-spot__stat">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                                        {popularSpot.likesCount ?? 0}
                                    </span>
                                    <span className="profile-popular-spot__stat">
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                        {popularSpot.favoritesCount ?? 0}
                                    </span>
                                    <Link to={`/spots/${popularSpot.id}`} className="profile-popular-spot__btn">Открыть точку</Link>
                                </div>
                            </div>
                        </article>
                    ) : (
                        <p className="profile-panel__empty">Пока нет откликов на ваших точках.</p>
                    )}
                </section>

                <section className="profile-panel" aria-labelledby="profile-favorites-title">
                    <div className="profile-contribution__header">
                        <h2 id="profile-favorites-title">Избранное</h2>
                        <span className="profile-contribution__legend">
                            {favorites.length} {favorites.length === 1 ? "точка" : "точек"} в списке
                        </span>
                    </div>
                    {!recentFavorites.length ? (
                        <p className="profile-panel__empty">Добавляйте точки в избранное на карте.</p>
                    ) : (
                        <ul className="profile-favorites-preview">
                            {recentFavorites.slice(0, 3).map((spot) => (
                                <li key={spot.id}>
                                    <Link to={`/spots/${spot.id}`}>
                                        <span className="profile-favorites-preview__thumb">
                                            {getSpotPreviewPhoto(spot) ? (
                                                <img src={buildImageUrl(getSpotPreviewPhoto(spot))} alt="" />
                                            ) : (
                                                <span aria-hidden>📍</span>
                                            )}
                                        </span>
                                        <span className="profile-favorites-preview__body">
                                            <strong>{spotName(spot)}</strong>
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section className="profile-panel profile-panel--wide" aria-labelledby="profile-recent-title">
                    <h2 id="profile-recent-title">Последние действия</h2>
                    {!recentActivity.length ? (
                        <p className="profile-panel__empty">Здесь появятся ваши точки, отчёты, комментарии и правки.</p>
                    ) : (
                        <ul className="profile-activity-feed">
                            {recentActivity.slice(0, 5).map((item) => (
                                <li key={item.id}>
                                    <span className={`profile-activity-feed__icon profile-activity-feed__icon--${item.tone}`}>{ACTIVITY_ICONS[item.tone]}</span>
                                    <div className="profile-activity-feed__body">
                                        {item.link ? (
                                            <Link to={item.link} style={{ paddingTop: "0", marginTop: "-5px" }}>{item.label}</Link>
                                        ) : (
                                            <span>{item.label}</span>
                                        )}
                                        <time dateTime={item.at}>{formatRelativeTime(item.at)}</time>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </div>

            <aside className="profile-tip" aria-label="Совет дня">
                <p>{TIP_OF_DAY}</p>
            </aside>
        </div>
    );
}
